/**
 * ENG-5732: Shared rendering contract for in-thread kanban progress cards.
 *
 * The control flow:
 *   1. Agent creates a kanban item with `source_integration='slack'` and a
 *      parseable `source_external_id` (`channel:thread_ts`).
 *   2. The API side (packages/api/src/lib/kanban-progress-card.ts) builds a
 *      KanbanCardState from the row, calls `renderKanbanSlackBlocks` to get
 *      a Block Kit payload, and posts it into the originating thread.
 *   3. On every transition (`/host/kanban` updates), the same driver
 *      re-renders the state and edits the existing message in place via
 *      chat.update — one persistent card per task.
 *   4. On terminal-and-fresh, the render includes Yes/No confirmation
 *      buttons (the existing `kanban-confirmation-slack` flow folds into
 *      the same card; see kanban-progress-card.ts for the merge).
 *
 * The Teams variant (renderKanbanTeamsCard, Adaptive Card v1.5) is tracked
 * separately under ENG-5748 — when it lands, it goes in this same file so
 * both renderers stay in lockstep with the shared KanbanCardState shape.
 *
 * Pure. No I/O. Unit-testable in isolation.
 */
import {
  encodeActionId,
  type SlackBlock,
  type SlackButtonElement,
  type SlackContextElement,
} from './slack-block-kit.js';
// ENG-9319: the surface capability check. It lives in core's artefact module
// beside the provider table rather than here, because the question "can Slack
// draw this image" is asked of a CATALOGUE url and the answer is the same
// wherever it is asked from.
import { canRenderImageInSlack, type ArtefactTone } from '../kanban/artefact.js';

/**
 * The state we render. Mirrors the subset of `agent_kanban_items` columns
 * the renderer cares about — *not* a row dump. New fields land here only
 * when both renderers (Slack + the future Teams Adaptive Card) need them.
 */
export interface KanbanCardState {
  /** Stable id used to build the result-detail URL when present. */
  kanbanItemId: string;
  /** Verbatim from `agent_kanban_items.title`. Truncated by the renderer. */
  title: string;
  /** Current status. Maps directly to the row's `status` column. */
  status: KanbanCardStatus;
  /**
   * ENG-5759: priority badge in the status context row. 1=high, 2=medium,
   * 3=low. Mirrors the kanban table's `priority` column. Optional —
   * driver omits when the row's priority is the default (2).
   */
  priority?: 1 | 2 | 3;
  /**
   * Optional one-line "what the agent is doing right now" string (e.g.
   * "Drafting reply"). Written by an explicit `kanban_progress`, or by the
   * auto-progress hook after the API reduces it to a verb (CS-1722,
   * `sanitizeAutoProgressStep`).
   *
   * CS-1722: there is intentionally NO `detail` field. It carried the card's
   * `description` into every chat surface as a "Task" row, and a description is
   * the agent's own brief: paths, verbatim instructions, reasoning. A requester
   * sent those into their Telegram DM asked twice for it to stop. It is removed
   * from the TYPE rather than just from the builder, so that no future call site
   * can hand a description to a renderer without first re-adding a field here
   * in review.
   */
  step?: string;
  /**
   * ENG-5759: source breadcrumb rendered as a small context line at
   * the bottom of the card (e.g. "From your Slack DM" or "From
   * #ops-alerts"). Distinct from `links` — this is plain text, not a
   * link.
   */
  sourceLabel?: string;
  /**
   * Terminal result text. Only rendered when status is terminal — we
   * deliberately don't surface partial results mid-stream because they're
   * speculative.
   */
  result?: string;
  /**
   * Optional rating (1–5) shown next to the result on terminal. Reserved
   * for ENG-5407 wiring; renderer just renders, doesn't validate.
   */
  rating?: number;
  /**
   * Optional outbound links — board URL, source thread, etc. Rendered in
   * a context block at the bottom.
   */
  links?: KanbanCardLink[];
  /**
   * ENG-8378 (CS-1540): what the card PRODUCED, as openable links.
   *
   * ITS OWN FIELD RATHER THAN MORE `links[]`, deliberately. Appending would
   * render, but not clearly, and the failure is asymmetric:
   *
   *   - appended LAST, an artefact lands in `links[1..]`, which Slack demotes to
   *     small breadcrumb text with no icon, Telegram truncates at 2 and Teams at
   *     3 — so the deliverable is the first thing dropped;
   *   - put FIRST, it silently bumps "Open card" out of the uncontested primary
   *     Actions slot, which is the one control every card is expected to have.
   *
   * `links` is navigation (where this card lives, where it came from);
   * `artefacts` is output (what it made). Two regions, no competition.
   *
   * Rendered ONLY on a terminal status, alongside `result`, for the same reason
   * `result` is: mid-flight artefacts are speculative.
   */
  artefacts?: KanbanCardArtefact[];
  /**
   * Confirmation actions to render inline on terminal. Set by the driver
   * only when the originating thread is fresh enough to act on (see
   * `evaluateConfirmationGuard`). Without this, the terminal card renders
   * status + result with no Yes/No.
   */
  confirm?: KanbanCardConfirmActions;
}

export type KanbanCardStatus =
  | 'backlog'
  | 'todo'
  | 'in_progress'
  | 'done'
  | 'failed'
  | 'cancelled'
  | 'needs_attention';

export interface KanbanCardLink {
  /** Link text — kept short, rendered as `<url|label>` in mrkdwn. */
  label: string;
  url: string;
}

/**
 * ENG-8378: one artefact on a rendered card.
 *
 * Structurally a link plus a kind, but kept separate from `KanbanCardLink` on
 * purpose: the two are rendered in different regions with different rules, and a
 * shared type would invite a renderer to treat them interchangeably, which is
 * the exact confusion this field exists to prevent.
 *
 * `emoji` and `label` arrive pre-resolved from `describeArtefactKind` so the
 * channel renderers stay dumb — no renderer needs to know the kind vocabulary,
 * and a new kind cannot half-land by teaching one surface and not the others.
 *
 * ENG-9319 EXTENDS THAT REASONING RATHER THAN CHANGING IT. The PROVIDER tier
 * (`describeArtefactProvider`, ENG-9248) and the catalogue logo behind it are
 * now pre-resolved here too, for the same reason and after exactly the failure
 * the paragraph above predicts: ENG-9248 taught the webapp chips a three-tier
 * badge and taught the channel renderers nothing, so the console showed `LN`
 * beside a Linear link while Slack showed the raw kind emoji on the same card.
 * The tier half-landed by being wired into one surface. Both new fields are
 * pre-resolved so that no renderer knows what a "provider" is, and so the logo
 * has ONE resolution path rather than one per surface.
 *
 * Both are OPTIONAL, deliberately: every surface must still render correctly
 * from `emoji` + `label` alone. A caller that cannot cheaply reach a catalogue,
 * or a provider that has no catalogue row (Slack, Google Drive, ourselves),
 * degrades to the monogram and then to the kind emoji — a designed fallback,
 * never a gap.
 */
export interface KanbanCardArtefact {
  /** Kind glyph. NEVER rendered without `label` beside it — see below. */
  emoji: string;
  /**
   * What the reader sees. The agent's own label if it gave one, otherwise the
   * kind's ("Dashboard", "Pull request").
   *
   * Mandatory, and that is the accessibility contract rather than a convenience:
   * a bare glyph is silent to a screen reader and indistinguishable at chip
   * size. `direct-chat-attachment.tsx` (icon + filename + size) is the in-repo
   * precedent.
   */
  label: string;
  url: string;
  /**
   * ENG-9319: the provider tier, pre-resolved from `describeArtefactProvider`.
   * Present only when the URL's host resolved to a known source application.
   *
   * `abbr` is the two-letter monogram ("LN"), which every surface uses as the
   * leading mark in place of the kind emoji, and which stays the fallback when
   * no logo exists or the surface cannot draw one. `tone` is carried for the
   * webapp's badge colouring and ignored by the chat surfaces, which have no
   * tone system to apply it to.
   */
  badge?: { abbr: string; label: string; tone: ArtefactTone };
  /**
   * ENG-9319: the vendor logo from the integration catalogue, when one exists.
   *
   * Resolved server-side (`resolveArtefactProviderIcons`) rather than derived,
   * so the url tracks `integration_definitions.icon` — including the ENG-8756
   * migration to a first-party mirror — instead of being a constant in this
   * tree that the logo-host ratchet cannot see.
   *
   * A surface renders it ONLY if it can actually draw it. Every catalogue logo
   * is an SVG today, and Slack's Block Kit image element cannot render SVG, so
   * the Slack renderer gates on `canRenderImageInSlack` and keeps the monogram
   * until the mirror (which normalises to PNG) is populated.
   */
  logoUrl?: string;
}

export interface KanbanCardConfirmActions {
  /** Existing confirmation callback_id — used to build the action_ids. */
  callbackId: string;
  /** 'done' or 'failed' — drives prompt copy. */
  outcome: 'done' | 'failed';
}

/**
 * Terminal statuses. Exported so callers (host-runtime.ts kanban write
 * path, the driver, tests) share one source of truth instead of guessing
 * which renderer cares about which status. Mirrors the API-side
 * `KANBAN_TERMINAL_STATES` set but framed for the *visual* contract — the
 * card style flips on these statuses regardless of whether the row also
 * happens to have a result.
 */
export const KANBAN_TERMINAL_STATUSES: ReadonlySet<KanbanCardStatus> = new Set([
  'done',
  'failed',
  'cancelled',
  'needs_attention',
]);

export function isTerminalKanbanStatus(s: KanbanCardStatus): boolean {
  return KANBAN_TERMINAL_STATUSES.has(s);
}

/**
 * PostgREST `in`-filter rendering of the terminal statuses, e.g.
 * `(done,failed,cancelled,needs_attention)`. Shared by the kanban dedupe
 * guards — the recurring-template spawn (kanban-recurring.ts, ENG-6439) and
 * the scheduled-task materialize (host-runtime.ts, ENG-7621) — so "open card"
 * means exactly the same thing on both paths and can't drift. Use as
 * `.not('status', 'in', KANBAN_TERMINAL_STATUS_INLIST)`.
 */
export const KANBAN_TERMINAL_STATUS_INLIST = `(${[...KANBAN_TERMINAL_STATUSES].join(',')})`;

// ──────────────────────────────────────────────────────────────────────
// Slack renderer
// ──────────────────────────────────────────────────────────────────────

/**
 * Map a status to its header emoji + visible label. Two slots so the
 * Slack header reads cleanly (":hourglass_flowing_sand: Working") and
 * the same data drives the (eventual) Teams Adaptive Card heading.
 */
export function describeKanbanStatus(status: KanbanCardStatus): {
  emoji: string;
  label: string;
} {
  switch (status) {
    case 'backlog':
      return { emoji: ':inbox_tray:', label: 'Backlog' };
    case 'todo':
      return { emoji: ':memo:', label: 'To do' };
    case 'in_progress':
      return { emoji: ':hourglass_flowing_sand:', label: 'Working' };
    case 'done':
      return { emoji: ':white_check_mark:', label: 'Done' };
    case 'failed':
      return { emoji: ':x:', label: 'Failed' };
    case 'cancelled':
      return { emoji: ':no_entry_sign:', label: 'Cancelled' };
    case 'needs_attention':
      return { emoji: ':pause_button:', label: 'Needs attention' };
    default: {
      // Exhaustiveness guard — never thrown at runtime if the union is
      // covered, but compiles to a useful error if a new status lands
      // without a renderer mapping.
      const _exhaustive: never = status;
      void _exhaustive;
      return { emoji: ':grey_question:', label: status };
    }
  }
}

/**
 * ENG-5759: human-readable priority badges. The kanban table's
 * `priority` column is an integer (1=high, 2=medium, 3=low) and the
 * old card rendered nothing for it. The board view shows a coloured
 * dot, so the Slack card mirrors that with a unicode equivalent +
 * label. The renderer omits the badge entirely when priority is
 * undefined (driver decides whether to surface medium as the default).
 */
function priorityBadge(priority: 1 | 2 | 3): string {
  switch (priority) {
    case 1:
      return '🔴 High';
    case 2:
      return '🟡 Medium';
    case 3:
      return '🟢 Low';
    default: {
      const _exhaustive: never = priority;
      void _exhaustive;
      return '';
    }
  }
}

/**
 * Build the Block Kit payload for an in-thread progress card. Pure.
 *
 * ENG-5759 / ENG-6010 layout, as divider-framed regions. CS-1722 removed the
 * *Task* region: it rendered the card's internal description into the
 * requester's thread, and there is no field left that could feed it.
 *
 *   [Header]   <title>
 *   [Context]  <emoji> <label>  ·  <priority>
 *   [Divider]                                     (before Progress/Result)
 *   [Section]  *Progress*\n<step | placeholder>   (non-terminal)
 *   [Section]  *Result:* <result>                 (terminal + result)
 *   [Context]  Rating: ★★★★                      (terminal + rating)
 *   [Divider]                                     (when actions follow)
 *   [Actions]  [ Open card ↗ ]  [ 👍 Looks good ] [ 👎 Not good enough ]
 *   [Context]  <sourceLabel> · <link2> · <link3>  (when present)
 */
export function renderKanbanSlackBlocks(state: KanbanCardState): SlackBlock[] {
  const { emoji, label } = describeKanbanStatus(state.status);
  const terminal = isTerminalKanbanStatus(state.status);
  const blocks: SlackBlock[] = [];

  // ── Header — Slack `header` blocks render the title large and
  // boldly. Cap at 150 chars per Slack's documented limit (the
  // SLACK_LIMITS constant in slack-block-kit.ts pins this).
  blocks.push({
    type: 'header',
    text: { type: 'plain_text', text: truncate(state.title, 150), emoji: true },
  });

  // ── Status + priority context row.
  {
    const parts: string[] = [`${emoji} *${label}*`];
    if (state.priority !== undefined) parts.push(priorityBadge(state.priority));
    blocks.push({
      type: 'context',
      elements: [{ type: 'mrkdwn', text: parts.join('  ·  ') }],
    });
  }

  // ── Body: *Progress* (live status) or *Result* (terminal), plus
  // *Produced* when there are artefacts. ENG-6010: separate, labeled
  // regions. Each is preceded by a divider; the actions row below
  // contributes the trailing one.
  //
  // ENG-9319 turned this from a list of SECTIONS into a list of REGIONS. A
  // region is one or more blocks that share a single leading divider. It was a
  // flat block list while every region happened to be exactly one section; the
  // *Produced* region now expands to a header plus one context block per
  // artefact, and a flat list would have put a divider between every artefact.
  const bodyRegions: SlackBlock[][] = [];

  // CS-1722: there is deliberately no *Task* region. It rendered the card's
  // `description`, which the agent writes for itself (file paths, verbatim
  // instructions, reasoning), into the requester's thread. The title is in the
  // header and "Open card" links to the full view. See `KanbanCardState`.

  if (terminal) {
    // *Result* — terminal only; replaces the live Progress region so it
    // doesn't compete with the outcome on completion.
    if (state.result) {
      // ENG-8798 AC 5: preview + an explicit "there is more", never a bare cut.
      // The note is escaped-safe by construction (our own text, digits and
      // ASCII punctuation only), so only the body goes through escapeMd.
      const preview = previewResult(state.result, 2_000, RESULT_PREVIEW_NOTE_BUDGET);
      const note = preview.truncated
        ? `\n\n${resultPreviewNote(preview.omittedWords, !!state.links?.[0])}`
        : '';
      bodyRegions.push([
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*Result:* ${escapeMd(preview.body)}${note}`,
          },
        },
      ]);
    }
    // ENG-8378 (CS-1540): *Produced* — the artefacts, in their OWN section
    // rather than appended to `links`, so they cannot be demoted to breadcrumb
    // text or bump "Open card" out of the Actions row. One per line, each
    // emoji + label, never a bare glyph.
    //
    // Rendered even when `result` is empty: "this card produced a thing" is
    // exactly as worth saying when the agent wrote no prose about it.
    //
    // ENG-9319: the leading mark is now the PROVIDER MONOGRAM when one resolved
    // ("LN <link>", not "🎫 <link>"), and the region becomes one context block
    // per artefact when — and only when — at least one logo is an image Slack
    // can actually draw.
    //
    // WHY THE LAYOUT IS CONDITIONAL RATHER THAN ALWAYS THE IMAGE LAYOUT. Every
    // logo in the integration catalogue is an SVG today (`logos.composio.dev`
    // serves `image/svg+xml`; GitHub's is a Wikimedia SVG), and Slack's Block
    // Kit image element cannot render SVG. Switching layout unconditionally
    // would trade one tidy section for five context blocks each carrying a
    // broken-image placeholder — strictly worse than the emoji Brad complained
    // about. ENG-8756's mirror normalises logos to PNG; when its bootstrap runs
    // and the seeds carry raster urls, `canRenderImageInSlack` starts returning
    // true and this lights up with no second PR.
    const artefacts = state.artefacts ?? [];
    if (artefacts.length > 0) {
      const drawable = artefacts.filter((a) => a.logoUrl && canRenderImageInSlack(a.logoUrl));
      if (drawable.length > 0) {
        // Header first, because a context block has no room for a region label
        // and an unlabelled row of links is the breadcrumb this region exists
        // not to be. One region, so ONE divider precedes the whole group.
        const region: SlackBlock[] = [
          { type: 'section', text: { type: 'mrkdwn', text: '*Produced*' } },
        ];
        for (const a of artefacts) {
          const link = `<${escapeSlackLinkUrl(a.url)}|${escapeMd(truncate(a.label, 60))}>`;
          const elements: SlackContextElement[] = [];
          // Per-artefact, not per-region: an artefact whose logo Slack cannot
          // draw sits in the same list with its monogram rather than being
          // dropped, so the list never has a hole in it.
          if (a.logoUrl && canRenderImageInSlack(a.logoUrl)) {
            elements.push({
              type: 'image',
              image_url: a.logoUrl,
              // The provider name where we have it ("Linear"), else the
              // artefact's own label. Never empty: Slack rejects the element,
              // and an unnamed mark is the accessibility failure this repo's
              // artefact docblocks all forbid.
              alt_text: a.badge?.label ?? a.label,
            });
            elements.push({ type: 'mrkdwn', text: link });
          } else {
            elements.push({ type: 'mrkdwn', text: `${a.badge?.abbr ?? a.emoji} ${link}` });
          }
          region.push({ type: 'context', elements });
        }
        bodyRegions.push(region);
      } else {
        bodyRegions.push([{
          type: 'section',
          text: {
            type: 'mrkdwn',
            text:
              `*Produced*\n` +
              artefacts
                .map(
                  (a) =>
                    `${a.badge?.abbr ?? a.emoji} <${escapeSlackLinkUrl(a.url)}|${escapeMd(truncate(a.label, 60))}>`,
                )
                .join('\n'),
          },
        }]);
      }
    }
  } else {
    // *Progress* — non-terminal. The latest step, or a placeholder so
    // the region (and any silence) is always visible even before the
    // agent's first kanban_progress call.
    const progress = state.step
      ? escapeMd(truncate(state.step, 240))
      : "⏳ Waiting for the agent's first update…";
    bodyRegions.push([
      {
        type: 'section',
        text: { type: 'mrkdwn', text: `*Progress*\n${progress}` },
      },
    ]);
  }

  // Precede each body region with a divider so *Progress* (or *Result*) and
  // *Produced* read as distinct regions rather than a run-on block.
  for (const region of bodyRegions) {
    blocks.push({ type: 'divider' });
    blocks.push(...region);
  }

  // ── Rating — reserved for ENG-5407.
  if (terminal && typeof state.rating === 'number') {
    const stars = '★'.repeat(Math.max(0, Math.min(5, Math.round(state.rating))));
    blocks.push({
      type: 'context',
      elements: [{ type: 'mrkdwn', text: `Rating: ${stars}` }],
    });
  }

  // ── Actions row — the primary link becomes an Open card button so
  // it reads as a CTA rather than as a context-line hyperlink. The
  // confirmation buttons (when terminal+fresh) sit alongside it.
  const primaryLink = state.links?.[0];
  const hasActions = !!primaryLink || (terminal && !!state.confirm);
  if (hasActions) {
    blocks.push({ type: 'divider' });
    const elements: SlackButtonElement[] = [];
    if (primaryLink) {
      elements.push({
        type: 'button',
        text: {
          type: 'plain_text',
          text: truncate(primaryLink.label, 60),
          emoji: true,
        },
        url: primaryLink.url,
        // Slack requires action_id on every button even when `url` is
        // set; the click still fires an interactivity event in
        // addition to opening the URL. The action_id is namespaced so
        // the existing interactivity handler can identify and ignore
        // the no-op (decodeActionId returns null on this prefix).
        action_id: `aug:kanban-open:${state.kanbanItemId}`,
      });
    }
    if (terminal && state.confirm) {
      // ENG-6015: thumbs labels; `value`/`action_id` tokens stay yes/no so
      // the interactivity handler + pending_interaction correlation are
      // unchanged. 👎 records −1 sentiment + a retro prompt, it does not
      // reopen the task (see applyKanbanConfirmation).
      elements.push({
        type: 'button',
        style: 'primary',
        text: { type: 'plain_text', text: '👍 Looks good', emoji: true },
        action_id: encodeActionId(state.confirm.callbackId, 'yes'),
        value: 'yes',
      });
      elements.push({
        type: 'button',
        style: 'danger',
        text: { type: 'plain_text', text: '👎 Not good enough', emoji: true },
        action_id: encodeActionId(state.confirm.callbackId, 'no'),
        value: 'no',
      });
    }
    blocks.push({ type: 'actions', elements });
  }

  // ── Bottom breadcrumb — source label + any non-primary links.
  // Combined into one context line to avoid card sprawl. Capped at 3
  // entries total so wide thread rendering stays sane.
  const breadcrumbParts: string[] = [];
  if (state.sourceLabel) {
    breadcrumbParts.push(escapeMd(truncate(state.sourceLabel, 80)));
  }
  if (state.links && state.links.length > 1) {
    for (const l of state.links.slice(1, 3 + (state.sourceLabel ? 0 : 1))) {
      breadcrumbParts.push(`<${l.url}|${escapeMd(truncate(l.label, 60))}>`);
    }
  }
  if (breadcrumbParts.length > 0) {
    blocks.push({
      type: 'context',
      elements: [{ type: 'mrkdwn', text: breadcrumbParts.join('  ·  ') }],
    });
  }

  return blocks;
}

/**
 * Plain-text fallback for the Slack `text` field — clients that can't
 * render Block Kit (or notification previews) see this string. The
 * driver passes it verbatim alongside the blocks.
 */
export function renderKanbanSlackFallbackText(state: KanbanCardState): string {
  const { label } = describeKanbanStatus(state.status);
  return `${label}: ${truncate(state.title, 200)}`;
}

// ──────────────────────────────────────────────────────────────────────
// Teams renderer (Adaptive Card v1.5)
// ──────────────────────────────────────────────────────────────────────
//
// ENG-5748: the Teams in-thread progress card. Same `KanbanCardState`
// inputs as the Slack renderer; emits an Adaptive Card v1.5 the Bot
// Framework `sendActivity` path can wrap as an attachment.
//
// Decisions baked in:
//   - v1.5 target, same baseline as packages/mcp/src/teams-adaptive-cards.ts
//     (ENG-5505); v1.6 would unlock Action.Execute but mobile support is
//     uneven.
//   - No confirm actions — per ENG-5732 OQ1, the Teams confirmation
//     stays SEPARATE from the progress card (no merge). The renderer
//     ignores `state.confirm` even when set; the existing Teams
//     confirmation post code path is untouched.
//   - Pure, inline Adaptive Card type definitions. The richer card
//     library in packages/mcp can't be imported from core (mcp depends
//     on core, not the other way round), and the renderer only needs a
//     thin slice of the schema.

/** Minimal Adaptive Card text block (v1.5). */
export interface TeamsTextBlock {
  type: 'TextBlock';
  text: string;
  wrap?: boolean;
  weight?: 'Default' | 'Lighter' | 'Bolder';
  size?: 'Default' | 'Small' | 'Medium' | 'Large' | 'ExtraLarge';
  isSubtle?: boolean;
  color?: 'Default' | 'Dark' | 'Light' | 'Accent' | 'Good' | 'Warning' | 'Attention';
}

/** Minimal Adaptive Card OpenUrl action (v1.5). */
export interface TeamsActionOpenUrl {
  type: 'Action.OpenUrl';
  title: string;
  url: string;
}

/** Minimal Adaptive Card root (v1.5). */
export interface TeamsAdaptiveCard {
  type: 'AdaptiveCard';
  $schema: 'http://adaptivecards.io/schemas/adaptive-card.json';
  version: '1.5';
  body: TeamsTextBlock[];
  actions?: TeamsActionOpenUrl[];
}

const ADAPTIVE_CARD_SCHEMA = 'http://adaptivecards.io/schemas/adaptive-card.json' as const;
const ADAPTIVE_CARD_VERSION = '1.5' as const;

/**
 * ENG-5748 CodeRabbit fix: Teams-specific emoji glyphs.
 *
 * `describeKanbanStatus` returns Slack mrkdwn shortcodes
 * (`:hourglass_flowing_sand:`, `:white_check_mark:`, …) which Slack's
 * client converts to glyphs. Teams' Adaptive Card TextBlock supports
 * a Markdown subset but does NOT translate Slack shortcodes — they
 * render as literal `:hourglass_flowing_sand:` text. The Teams
 * renderer needs the raw Unicode glyph instead.
 *
 * Map intentionally chosen to read identically to the Slack shortcode
 * set so a reader switching between Teams + Slack sees the same
 * status iconography.
 */
const TEAMS_STATUS_EMOJI: Record<KanbanCardStatus, string> = {
  backlog: '📥',
  todo: '📝',
  in_progress: '⏳',
  done: '✅',
  failed: '❌',
  cancelled: '🚫',
  needs_attention: '⏸️',
};

/** Exported for tests + future Teams-only surfaces. */
export function describeKanbanStatusForTeams(status: KanbanCardStatus): {
  emoji: string;
  label: string;
} {
  return {
    emoji: TEAMS_STATUS_EMOJI[status],
    label: describeKanbanStatus(status).label,
  };
}

/**
 * Map a kanban status to its Adaptive Card color tag. Mirrors the
 * Slack emoji + label split but flows the meaning into the schema's
 * `color` enum so the surface shows a coloured bar in Teams' UI.
 */
function statusColor(status: KanbanCardStatus): TeamsTextBlock['color'] {
  switch (status) {
    case 'backlog':
    case 'todo':
      return 'Default';
    case 'in_progress':
      return 'Accent';
    case 'done':
      return 'Good';
    case 'failed':
      return 'Attention';
    case 'cancelled':
      return 'Warning';
    case 'needs_attention':
      return 'Warning';
    default: {
      const _exhaustive: never = status;
      void _exhaustive;
      return 'Default';
    }
  }
}

/**
 * Build the Adaptive Card v1.5 payload for an in-thread Teams progress
 * card. Pure; no I/O.
 *
 * Layout:
 *   <emoji+label>     (Bolder, colored by status)
 *   <title>           (Large)
 *   Step: <step>      (non-terminal, when set)
 *   Result: <result>  (terminal only, when set)
 *   (CS-1722: no description block; the card has no field that could feed one)
 *   ★★★★              (when rating set, terminal only)
 *   [Open card] [Source thread]   (links)
 */
export function renderKanbanTeamsCard(state: KanbanCardState): TeamsAdaptiveCard {
  const { emoji, label } = describeKanbanStatusForTeams(state.status);
  const terminal = isTerminalKanbanStatus(state.status);
  const body: TeamsTextBlock[] = [];

  // Header line — emoji-prefixed label, coloured by status. `label`
  // comes from a fixed enum (`describeKanbanStatusForTeams`) so it
  // doesn't need escaping; the emoji is a Unicode glyph that
  // Markdown leaves alone.
  body.push({
    type: 'TextBlock',
    text: `${emoji} ${label}`,
    weight: 'Bolder',
    color: statusColor(state.status),
    wrap: true,
  });

  // Title — large, always present. ENG-5748 CodeRabbit fix: escape
  // Adaptive-Card Markdown so an agent-controlled string like
  // `[click](https://evil.example)` can't slip a clickable link or
  // bold formatting into the card.
  body.push({
    type: 'TextBlock',
    text: escapeAdaptiveMarkdown(truncate(state.title, 240)),
    size: 'Large',
    wrap: true,
  });

  if (!terminal && state.step) {
    body.push({
      type: 'TextBlock',
      text: `**Step:** ${escapeAdaptiveMarkdown(truncate(state.step, 240))}`,
      wrap: true,
    });
  }
  // CS-1722: no description block. See `KanbanCardState`.

  if (terminal && state.result) {
    // ENG-8798 AC 5. Teams was named out of scope on the issue ("2 completed
    // cards in 90 days; do not spend on it") — but that was about not building
    // Teams-SPECIFIC work, and this is the shared helper at a call site that
    // already exists. Skipping it would deliberately leave one of the three
    // renderers cutting mid-sentence to save a single line, and would make the
    // next reader wonder which behaviour is the intended one.
    const preview = previewResult(state.result, 2_000, RESULT_PREVIEW_NOTE_BUDGET);
    const note = preview.truncated
      ? `\n\n${resultPreviewNote(preview.omittedWords, !!state.links?.[0])}`
      : '';
    body.push({
      type: 'TextBlock',
      text: `**Result:** ${escapeAdaptiveMarkdown(preview.body)}${note}`,
      wrap: true,
    });
  }

  // ENG-8378: **Produced** — its own TextBlock, above the rating and outside the
  // `actions` array. Teams caps actions at 3, which `links` already competes
  // for; an artefact in there would be the thing that falls off. Same reasoning
  // as ENG-8798's note above on why Teams still gets the shared behaviour: this
  // is the shared field at a call site that already exists, and leaving one of
  // three renderers silently different is worse than the line it saves.
  if (terminal && (state.artefacts?.length ?? 0) > 0) {
    body.push({
      type: 'TextBlock',
      text:
        '**Produced**\n\n' +
        (state.artefacts ?? [])
          // ENG-9319: provider monogram when one resolved, else the kind emoji.
          // No image: Teams' Adaptive Card markdown does not render one inside a
          // TextBlock, and an `Image` element would need its own body entry and
          // its own layout decision. The monogram is the same signal in text.
          .map((a) => `${a.badge?.abbr ?? a.emoji} [${escapeAdaptiveMarkdown(truncate(a.label, 60))}](${a.url})`)
          .join('\n\n'),
      wrap: true,
    });
  }

  if (terminal && typeof state.rating === 'number') {
    const stars = '★'.repeat(Math.max(0, Math.min(5, Math.round(state.rating))));
    body.push({
      type: 'TextBlock',
      text: `Rating: ${stars}`,
      isSubtle: true,
      size: 'Small',
    });
  }

  const card: TeamsAdaptiveCard = {
    type: 'AdaptiveCard',
    $schema: ADAPTIVE_CARD_SCHEMA,
    version: ADAPTIVE_CARD_VERSION,
    body,
  };

  // Action.OpenUrl per link, capped at 3 so the action row stays readable.
  if (state.links && state.links.length > 0) {
    card.actions = state.links.slice(0, 3).map(
      (l): TeamsActionOpenUrl => ({
        type: 'Action.OpenUrl',
        title: truncate(l.label, 60),
        url: l.url,
      }),
    );
  }

  // NOTE: `state.confirm` is intentionally ignored. Per ENG-5732 OQ1,
  // the Teams confirmation stays a SEPARATE card on terminal — the
  // existing Teams confirmation flow handles the Yes/No surface. If
  // that ever changes (merged-card pattern from Slack), add an
  // Action.Submit pair here keyed off `state.confirm.callbackId`.

  return card;
}

/**
 * Anchor persisted at `agent_kanban_items.metadata.progress_card.teams`
 * once the (not-yet-wired) Teams driver branch posts the initial card.
 * Shape pinned now so the renderer + tests stay in lockstep with the
 * future driver work. ENG-5748b will own the Bot Framework wiring;
 * see the file header note.
 */
export interface TeamsProgressCardAnchor {
  /** Bot Framework conversation id (URL path component). */
  conversation_id: string;
  /** Region-specific Bot Framework endpoint from the inbound activity. */
  service_url: string;
  /** Activity id of OUR posted card — the target of subsequent PUTs. */
  activity_id: string;
}

// ──────────────────────────────────────────────────────────────────────
// Telegram renderer (plain text)
// ──────────────────────────────────────────────────────────────────────
//
// ENG-6266 spike: the Telegram in-thread progress card. Same
// `KanbanCardState` inputs as the Slack + Teams renderers; emits a single
// plain-text string the Telegram `sendMessage` / `editMessageText` path
// sends (and edits in place) into the originating chat.
//
// Decisions baked in (see docs/spikes/eng-6266-telegram-kanban-live-wip.md):
//   - HTML parse_mode, used for ONE thing: a `<b>`-bolded title. The spike
//     originally shipped plain text with no parse_mode to dodge injection and
//     brittle "can't parse entities" 400s; ENG-6309 added the bold title.
//     HTML mode is the safe way to do it — it treats only `&`, `<`, `>` as
//     special, so escaping exactly those three on every agent-controlled field
//     (escapeTelegramHtml) keeps the injection surface closed and a stray
//     bracket from ever 400-ing, while MarkdownV2 would force escaping ~18
//     metacharacters. The driver sends the body with `parse_mode: 'HTML'`.
//   - Unicode status glyphs (shared with the Teams set) rather than Slack
//     `:shortcode:` text, which Telegram would render literally.
//   - No inline confirmation buttons here. Like Teams (ENG-5732 OQ1), the
//     Telegram confirmation stays a SEPARATE message
//     (postKanbanConfirmationTelegram) on terminal; this renderer ignores
//     `state.confirm`. The card itself updates in place across the WIP
//     lifecycle; the Yes/No surface is posted once, at the end.
//
// The companion driver (post + editMessageText, anchor at
// metadata.progress_card.telegram = { chat_id, message_id }) is the
// follow-up implementation issue — this renderer is the reusable core it
// will consume, and the spike prototype drives it end-to-end against the
// live Bot API.

/**
 * Telegram status glyphs. Identical Unicode set to the Teams renderer —
 * Telegram, like Teams, can't translate Slack `:shortcode:` mrkdwn and
 * would show the literal text. Kept as its own map so a future
 * Telegram-only iconography tweak doesn't perturb Teams.
 */
const TELEGRAM_STATUS_EMOJI: Record<KanbanCardStatus, string> = {
  backlog: '📥',
  todo: '📝',
  in_progress: '⏳',
  done: '✅',
  failed: '❌',
  cancelled: '🚫',
  needs_attention: '⏸️',
};

/** Exported for tests + the spike prototype. */
export function describeKanbanStatusForTelegram(status: KanbanCardStatus): {
  emoji: string;
  label: string;
} {
  return {
    emoji: TELEGRAM_STATUS_EMOJI[status],
    label: describeKanbanStatus(status).label,
  };
}

/**
 * Build the body for a Telegram in-chat progress card. Pure; no I/O. The
 * driver sends this with `parse_mode: 'HTML'`, then edits the same message_id
 * in place on each transition.
 *
 * Layout (blank lines separate regions, since Telegram has no dividers — the
 * title sits in its own region so it gets a blank line above and below):
 *   <emoji> <label>  ·  <priority>
 *
 *   <b><title></b>
 *
 *   Progress: <step | placeholder>                  (non-terminal)
 *   Result: <result>                                (terminal + result)
 *
 *   Rating: ★★★★                                    (terminal + rating)
 *
 *   <sourceLabel>                                   (breadcrumb)
 *
 * Returns a string. Callers MUST send it with `parse_mode: 'HTML'`. Every
 * agent-controlled field is run through escapeTelegramHtml, so the only live
 * markup is the `<b>` wrapping the title.
 */
export function renderKanbanTelegramCard(state: KanbanCardState): string {
  const { emoji, label } = describeKanbanStatusForTelegram(state.status);
  const terminal = isTerminalKanbanStatus(state.status);

  // Status line: glyph + label, plus a priority badge (own glyph + word) joined
  // with a middot. Both halves are static, renderer-controlled strings — no
  // agent input — so they need no escaping.
  const statusLine =
    state.priority !== undefined
      ? `${emoji} ${label}  ·  ${priorityBadge(state.priority)}`
      : `${emoji} ${label}`;

  // Each region is a group of lines; regions are joined with a blank line.
  const regions: string[] = [];

  // Region 1: status line.
  regions.push(statusLine);

  // Region 2: the title, on its own line and given breathing room — it's its
  // own region, so a blank line sits above and below it — and bolded. The <b>
  // tag is the one piece of HTML markup we emit; the title is agent-controlled,
  // so it's HTML-escaped before going inside the tag.
  regions.push(`<b>${escapeTelegramHtml(truncate(state.title, 240))}</b>`);

  // Region 3 (Task) was removed by CS-1722. See `KanbanCardState`.

  // Region 4: Progress (non-terminal) or Result (terminal). The progress
  // placeholder keeps the region — and any silence — visible before the
  // agent's first step, matching the Slack/Teams behaviour.
  if (terminal) {
    if (state.result) {
      // ENG-8798 AC 5. Telegram's link lives in the inline keyboard
      // (renderKanbanTelegramInlineKeyboard), built from the same state.links —
      // so "open the card" is honest here on exactly the same condition.
      const preview = previewResult(state.result, 2_000, RESULT_PREVIEW_NOTE_BUDGET);
      const note = preview.truncated
        ? `\n\n${resultPreviewNote(preview.omittedWords, !!state.links?.[0])}`
        : '';
      regions.push(`Result: ${escapeTelegramHtml(preview.body)}${note}`);
    }
  } else {
    const progress = state.step
      ? escapeTelegramHtml(truncate(state.step, 240))
      : "⏳ Waiting for the agent's first update…";
    regions.push(`Progress: ${progress}`);
  }

  // Region 4b: Produced — ENG-8378. Labels ONLY, no URLs: the links themselves
  // are inline-keyboard buttons (see renderKanbanTelegramInlineKeyboard), and a
  // URL in the body is exactly what ENG-6302 removed because Telegram unfurls it
  // into a large preview card.
  //
  // So why say it at all, when the buttons are right there? Because a row of
  // buttons does not say WHAT it is. This is the text that names the region, and
  // the ticket's accessibility rule is explicit that "this card produced an
  // artefact" must be conveyed by text or shape rather than by a visual
  // affordance alone.
  if (terminal && (state.artefacts?.length ?? 0) > 0) {
    const names = (state.artefacts ?? [])
      // ENG-9319: provider monogram when one resolved, else the kind emoji.
      .map((a) => `${a.badge?.abbr ?? a.emoji} ${escapeTelegramHtml(truncate(a.label, 60))}`)
      .join(', ');
    regions.push(`Produced: ${names}`);
  }

  // Region 5: rating (terminal only, when set).
  if (terminal && typeof state.rating === 'number') {
    const stars = '★'.repeat(Math.max(0, Math.min(5, Math.round(state.rating))));
    regions.push(`Rating: ${stars}`);
  }

  // Region 6: breadcrumb — source label only. Links used to ride here as bare
  // `label: url` text, but Telegram auto-links + unfurls those into a large
  // link-preview card (ENG-6302). The links now render as inline-keyboard
  // buttons via renderKanbanTelegramInlineKeyboard() instead, so the body
  // carries no URL to unfurl.
  if (state.sourceLabel) {
    regions.push(escapeTelegramHtml(truncate(state.sourceLabel, 80)));
  }

  return regions.join('\n\n');
}

/** A Telegram `reply_markup` inline keyboard: rows of URL buttons. */
export interface TelegramInlineKeyboardMarkup {
  inline_keyboard: Array<Array<{ text: string; url: string }>>;
}

/**
 * ENG-6302: build the Telegram inline keyboard for a kanban card from its
 * links — one URL button per link (e.g. "Open card"), each on its own row.
 * Returns `undefined` when the card has no links, so the driver can omit
 * `reply_markup` entirely.
 *
 * The driver MUST attach this to BOTH sendMessage and every editMessageText —
 * Telegram strips the keyboard from an edited message unless reply_markup is
 * re-sent. Pairing the button with `link_preview_options.is_disabled` keeps
 * the card compact: no in-body URL text, no unfurled preview.
 */
export function renderKanbanTelegramInlineKeyboard(
  state: KanbanCardState,
): TelegramInlineKeyboardMarkup | undefined {
  const rows = (state.links ?? [])
    .slice(0, 2)
    .map((l) => [{ text: truncate(l.label, 60), url: l.url }]);

  // ENG-8378: artefacts become their own buttons, BELOW the navigation links.
  //
  // Telegram is the one surface where they cannot go in the body: ENG-6302
  // removed in-body URLs precisely because Telegram auto-links and unfurls them
  // into a large preview card, and the driver pairs this keyboard with
  // `link_preview_options.is_disabled`. Putting a deliverable link back in the
  // text would re-open that.
  //
  // Not capped at 2 like the links are. That cap is about navigation noise —
  // "Open card", "Open source thread" — whereas these ARE the deliverables, and
  // dropping one to keep a keyboard short is the failure this field exists to
  // prevent. The count is already bounded at 5 by MAX_ARTEFACTS at write time.
  //
  // Terminal-only, matching the body's Result region: mid-flight artefacts are
  // speculative, and a button is a stronger claim than a line of text.
  if (isTerminalKanbanStatus(state.status)) {
    for (const artefact of state.artefacts ?? []) {
      // ENG-9319: monogram over emoji, and the 60-char button cap still applies
      // to the whole `mark + label` string — Telegram truncates a longer one
      // itself, and where it cuts is not ours to choose.
      rows.push([
        {
          text: truncate(`${artefact.badge?.abbr ?? artefact.emoji} ${artefact.label}`, 60),
          url: artefact.url,
        },
      ]);
    }
  }

  // Undefined rather than an empty keyboard, so the driver can omit
  // `reply_markup` entirely — Telegram renders a stray empty markup as a gap.
  return rows.length > 0 ? { inline_keyboard: rows } : undefined;
}

// ──────────────────────────────────────────────────────────────────────
// helpers — kept private to the module
// ──────────────────────────────────────────────────────────────────────

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

/**
 * How much of a card's `result` fits in a channel message, and how much is
 * left over — ENG-8798 AC 5.
 *
 * ## What was wrong
 *
 * A completed card's `result` went out as `truncate(result, 2_000)`: a hard
 * slice at the character budget with a bare `…` appended. Measured over 90 days
 * of prod, results average 1,330 characters and p95 is 4,331, so **5,489 cards
 * (23.9%) were cut** — and for 85.7% of completed cards the result IS the
 * deliverable, not a status note. The reader got most of a brief, severed
 * mid-sentence, with nothing to say that anything was missing: one `…` at the
 * end of a long message reads as the agent trailing off, not as truncation.
 *
 * ## What this returns instead
 *
 * The visible portion plus a COUNT of what was withheld, so the renderer can
 * say so in its own syntax. Three things it does deliberately:
 *
 * 1. **Reserves room for the note inside the budget.** The note is part of the
 *    message, so appending it after cutting at `budget` would overrun — Slack's
 *    section text hard-limits at 3,000 and Telegram's whole message at 4,096.
 *    A truncation notice that itself causes a delivery failure is worse than
 *    the truncation.
 * 2. **Backs off to a boundary.** Cutting mid-word makes the last line look
 *    like a typo rather than a cut. It prefers a paragraph break, then a line
 *    break, then whitespace, searching only the last quarter of the budget so a
 *    text with no boundaries (a URL, a base64 blob) still gets close to a full
 *    preview instead of collapsing to nothing.
 * 3. **Counts the remainder in WORDS.** Slice 1's webapp panel reports
 *    `412 words` for exactly this reason, recorded there: nobody reads "2,331
 *    characters" and knows whether that is a paragraph or a report. Using the
 *    same unit means the Slack card and the board agree about the same result.
 *
 * The caller supplies the note wording, because "open the card" is only honest
 * when a link exists — see each renderer's call site.
 */
export interface ResultPreview {
  /** The portion to render. Not yet escaped — the renderer owns that. */
  body: string;
  /** Whether anything was withheld. */
  truncated: boolean;
  /** Words withheld. 0 when `truncated` is false. */
  omittedWords: number;
}

/**
 * @param result   the full stored result
 * @param budget   characters available for body AND note together
 * @param noteCost characters the renderer's note will occupy
 */
export function previewResult(result: string, budget: number, noteCost: number): ResultPreview {
  if (result.length <= budget) {
    return { body: result, truncated: false, omittedWords: 0 };
  }

  // Never let the note squeeze the body to nothing: with a large noteCost and a
  // small budget the reservation could go negative, and a preview of zero
  // characters tells the reader strictly less than the old hard cut did.
  const room = Math.max(Math.floor(budget / 2), budget - noteCost);

  const window = Math.floor(room / 4);
  const head = result.slice(0, room);
  // Paragraph, then line. `lastIndexOf` over the whole head, then a window
  // check, rather than a regex scan — the boundary must be near the END of the
  // preview to be worth using; one 300 characters back would throw away text
  // the reader could have had.
  let cut = -1;
  for (const sep of ['\n\n', '\n']) {
    const at = head.lastIndexOf(sep);
    if (at > room - window) {
      cut = at;
      break;
    }
  }
  // Then ANY whitespace, not just an ASCII space. The first version listed ' '
  // as the third separator, which quietly excluded tabs and non-breaking
  // spaces — and both are ordinary in this field: an agent emitting a
  // tab-aligned table, or prose carrying U+00A0 because it was lifted from a
  // web page (a competitor brief, say). Neither matched, so the back-off was
  // skipped and the cut landed mid-word — exactly the outcome this function
  // exists to avoid, on the inputs least likely to be noticed (CodeRabbit,
  // PR #4546).
  //
  // A backwards scan rather than a lastIndexOf-per-separator or a lookahead
  // regex: `\s` has no fixed spelling to search for, the loop is bounded by
  // `window` (~484 characters), and it cannot backtrack.
  if (cut < 0) {
    const floor = Math.max(0, room - window);
    for (let i = head.length - 1; i >= floor; i--) {
      if (/\s/.test(head[i] as string)) {
        cut = i;
        break;
      }
    }
  }
  const body = (cut > 0 ? head.slice(0, cut) : head).trimEnd();

  // Count the REMAINDER, not the whole. `result.slice(body.length)` rather than
  // (total − shown) so a boundary back-off is charged to the omitted side and
  // the two numbers always reconcile to the reader.
  const omittedWords = (result.slice(body.length).match(/\S+/g) ?? []).length;

  return { body, truncated: true, omittedWords };
}

/**
 * Plain-English tail for a truncated result.
 *
 * `hasLink` is load-bearing: three of these renderers are always called with an
 * "Open card" link today, but `KanbanCardState.links` is optional and these
 * renderers are exported from core, so the wording must not promise a
 * destination that isn't in the message. Telling someone to open a card that
 * has no link is a worse failure than saying nothing — they go looking for a
 * button that was never rendered.
 */
export function resultPreviewNote(omittedWords: number, hasLink: boolean): string {
  const amount = omittedWords === 1 ? '1 more word' : `${omittedWords.toLocaleString('en-US')} more words`;
  return hasLink ? `[+${amount} — open the card to read the rest]` : `[+${amount} not shown here]`;
}

/**
 * Worst-case width of `resultPreviewNote`, for budget reservation.
 *
 * Deliberately a constant rather than a measurement of the real note: the note
 * cannot be built until the body is cut, and the body cannot be cut until the
 * note's width is reserved. Sized for a 7-digit word count (a 25,998-character
 * result — the measured maximum — is roughly 4,000 words, so this has ample
 * headroom) and re-asserted by a test against the real formatter.
 */
export const RESULT_PREVIEW_NOTE_BUDGET = 64;

/**
 * Same escape policy as kanban-confirmation-slack: chevrons + ampersand
 * become HTML entities so Slack's mrkdwn parser doesn't read them as
 * tags; backticks lose their formatting power (replaced with single
 * quote) so an agent-controlled string can't inject inline code.
 */
function escapeMd(s: string): string {
  return s.replace(/[<>&`]/g, (c) =>
    c === '<' ? '&lt;' :
    c === '>' ? '&gt;' :
    c === '&' ? '&amp;' :
    "'",
  );
}

/**
 * ENG-8378: make a URL safe to sit inside Slack's `<url|label>` link syntax,
 * WITHOUT re-encoding it.
 *
 * The first version of this called `encodeURI()`, which CodeRabbit correctly
 * flagged: `encodeURI` escapes a bare `%`, so a URL that already contains a
 * percent escape comes out double-encoded. `…/a%2Fb` becomes `…/a%252Fb`, and
 * the link silently resolves somewhere else — or nowhere. Presigned URLs,
 * anything with an encoded path segment, and any query string carrying `%2B`
 * for a literal plus are all ordinary, so this is not an edge case.
 *
 * The URL arriving here is already normalised through `new URL()` by
 * `validateArtefactUrl` at write time, so it is well-formed. What it still needs
 * is protection from the three characters that would terminate or split the
 * mrkdwn link construct itself. Those are percent-encoded individually; every
 * other byte, including existing `%HH` sequences, is left exactly as stored.
 *
 * `&` is deliberately NOT touched. It is legal and load-bearing in a query
 * string, Slack does not treat it as a link delimiter, and entity-escaping it
 * here would corrupt every multi-parameter URL — the same double-encoding
 * mistake in a different costume.
 */
function escapeSlackLinkUrl(url: string): string {
  return url.replace(/[<>|]/g, (c) =>
    c === '<' ? '%3C' :
    c === '>' ? '%3E' :
    '%7C',
  );
}

/**
 * ENG-5748 CodeRabbit fix: Adaptive-Card-Markdown escape, distinct
 * from `escapeMd` (which is Slack-mrkdwn-specific).
 *
 * Teams' Adaptive Card `TextBlock.text` runs a Markdown subset that
 * includes `[link](url)`, `*bold*`, `_italic_`, and backticks. An
 * agent-controlled string like `[click](https://evil.example)` would
 * otherwise render as a clickable link in the card — the phishing
 * vector the reviewer flagged.
 *
 * Backslash-escape the markdown metacharacters: `\`, `[`, `]`, `*`,
 * `_`, `` ` ``, `(`, `)`. The backslash must be escaped FIRST so we
 * don't double-escape on a second pass. Reference: AC v1.5
 * authoring docs + the CodeRabbit web search captured on PR #1533.
 */
function escapeAdaptiveMarkdown(s: string): string {
  return s.replace(/[\\[\]*_`()]/g, (c) => `\\${c}`);
}

/**
 * Telegram HTML parse_mode escape. The card is sent with `parse_mode: 'HTML'`
 * so the renderer can emit a `<b>`-bolded title. HTML mode treats only `&`,
 * `<`, and `>` as special, so escaping exactly those three on every
 * agent-controlled field (title, step, result, sourceLabel) both
 * closes the injection surface (an agent string like `</b><a href=…>` becomes
 * inert text) AND prevents Telegram's "can't parse entities" 400 on an
 * unbalanced bracket. Ampersand is replaced first so we don't double-escape
 * the entities we introduce.
 */
function escapeTelegramHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
